import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import {
  operations as curriculumCourseOperations,
  selectors as curriculumCourseSelectors,
} from 'store/curriculumCourseManager'
import { transformCurriculumCourseTree } from 'layers/content/v3/transforms/transformCurriculumCourseTree'
import {
  applyLessonCompletionToLinkSummaries,
  buildLeafNodeIdMap,
  selectClassRegistrationFromResponse,
} from 'pages/UnitV3/data'

/**
 * Derives lesson completion status for each link summary on a unit page by
 * reading the current instructor registration data from the LMS store.
 *
 * Fetches /v2/instructor/me if the required program slice is not yet in the
 * store. Also fetches the curriculum-service course tree via the coursePointer
 * so that Contentful entry IDs (courseNodeEntryPoint) can be translated to LMS
 * leaf node IDs for accurate completion matching — mirroring the pattern used
 * by `useMarkCompleteV3`.
 *
 * @param {Object}          options
 * @param {string|null}     options.instructorDataKey  Top-level key in the
 *   instructor/me response that corresponds to the current program (e.g.
 *   'Dwb'). Pass null or undefined to skip completion lookup entirely.
 * @param {Array}           options.linkSummaries      Raw link summaries from
 *   curriculum-service.
 * @param {number|string|null} options.siteId          Current site id.
 * @param {string|null}     options.gradeSegment       Grade URL segment (e.g.
 *   'grade-5').
 * @param {string|null}     options.courseCode         Resolved course code.
 * @param {Object|null}     options.coursePointer      coursePointer from the
 *   curriculum-service routeContent response. Used to fetch the course tree so
 *   courseNodeEntryPoint → leafNodeId mapping can be built.
 * @param {string|null}     options.contentGroupCode   Content group code (e.g.
 *   'dwb'). Used as the Redux state-key prefix when caching the course tree.
 *
 * @returns {{ linkSummaries: Array, isLoading: boolean }}
 *   `linkSummaries` has `lessonComplete` applied to each entry.
 *   `isLoading` is true while instructor data or the course tree are in flight.
 */
export function useUnitLessonCompletion({
  instructorDataKey,
  linkSummaries,
  siteId,
  gradeSegment,
  courseCode,
  coursePointer,
  contentGroupCode,
}) {
  const dispatch = useDispatch()
  const location = useLocation()

  // --- Instructor data ---

  const isFetchingOrCreating = useSelector(
    state => !!state.lmsManager?.isFetchingOrCreatingProgramFlex,
  )

  const instructorData = useSelector(
    lmsSelectors.selectInstructorData(instructorDataKey),
  )

  useEffect(() => {
    if (!instructorDataKey) return
    if (isFetchingOrCreating || instructorData) return
    dispatch(lmsOperations.fetchInstructorProgramFlexGet())
  }, [dispatch, instructorDataKey, isFetchingOrCreating, instructorData])

  // --- Course tree ---

  const coursePointerCode = useMemo(() => {
    const q = coursePointer?.query || {}
    return q.courseCode || q.course_code || null
  }, [coursePointer])

  const courseTreeEntry = useSelector(
    curriculumCourseSelectors.selectCurriculumCourseTree(
      contentGroupCode,
      coursePointerCode,
      siteId,
    ),
  )

  const courseTreeData = courseTreeEntry?.data
  const courseTreeLoading = courseTreeEntry?.loading
  const courseTreeError = courseTreeEntry?.error

  useEffect(() => {
    if (!instructorDataKey) return
    if (!coursePointer || !coursePointerCode || siteId == null) return
    if (courseTreeData || courseTreeLoading || courseTreeError) return
    dispatch(
      curriculumCourseOperations.fetchCourseTreeFromPointer({
        coursePointer,
        contentGroupCode,
        siteId,
      }),
    )
  }, [
    coursePointer,
    coursePointerCode,
    contentGroupCode,
    siteId,
    instructorDataKey,
    dispatch,
    courseTreeData,
    courseTreeLoading,
    courseTreeError,
  ])

  const leafNodeIdMap = useMemo(() => {
    if (!courseTreeData) return {}
    const tree = transformCurriculumCourseTree(courseTreeData)
    return buildLeafNodeIdMap(tree)
  }, [courseTreeData])

  /**
   * Ordered list of leaf node IDs taken directly from
   * `subTreeNodes.siteEducator[0].leafExternalIds` in the course tree response.
   *
   * This is the primary completion-matching path for programs (e.g. DWB) where
   * curriculum-service Summary entries do not include `courseNodeConnector`.
   * The position of each entry matches the position of the corresponding link
   * summary so completion can be resolved by index.
   */
  const orderedLeafNodeIds = useMemo(() => {
    if (!courseTreeData) return []
    const subTreeNodes =
      courseTreeData.subTreeNodes || courseTreeData.sub_tree_nodes || {}
    const siteEducatorScopes =
      subTreeNodes.siteEducator || subTreeNodes.site_educator || []
    if (!Array.isArray(siteEducatorScopes)) return []
    const lessonScope = siteEducatorScopes.find(
      scope =>
        scope.lessonInteractionType === 'lesson' ||
        scope.lesson_interaction_type === 'lesson',
    )
    return lessonScope?.leafExternalIds || lessonScope?.leaf_external_ids || []
  }, [courseTreeData])

  // --- Class registration + completion ---

  const classId = useMemo(() => {
    const search =
      location?.search ||
      (typeof window !== 'undefined' ? window?.location?.search || '' : '')
    return new URLSearchParams(search).get('class')
  }, [location?.search])

  const classRegistration = useMemo(
    () =>
      selectClassRegistrationFromResponse({
        instructorResponse: instructorData,
        classId,
        siteId,
        gradeSegment,
        courseCode,
      }),
    [instructorData, classId, siteId, gradeSegment, courseCode],
  )

  const enrichedLinkSummaries = useMemo(
    () =>
      applyLessonCompletionToLinkSummaries({
        linkSummaries,
        classRegistration,
        leafNodeIdMap,
        orderedLeafNodeIds,
      }),
    [linkSummaries, classRegistration, leafNodeIdMap, orderedLeafNodeIds],
  )

  const hasCoursePointerDeps =
    !!instructorDataKey && !!coursePointerCode && siteId != null
  const isCourseTreeLoading =
    hasCoursePointerDeps && !courseTreeData && !courseTreeError
  const isLoading =
    !!instructorDataKey && (!instructorData || isCourseTreeLoading)

  return {
    linkSummaries: enrichedLinkSummaries,
    isLoading,
  }
}
